/**
 * AlphaGate Component
 *
 * Authentication gate for alpha access to Space Operator Copilot.
 * Blocks unauthenticated users and provides Magic Link login + Access Request flow.
 *
 * Features:
 * - Dev bypass via VITE_SKIP_AUTH=true or Vite DEV mode
 * - Magic Link authentication via Supabase
 * - Access request form for new users
 * - Session persistence across page refreshes
 * - Retro mission control aesthetic
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Rocket, Mail, Loader2, Send, UserPlus } from 'lucide-react';
import { z } from 'zod';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useToast } from '../hooks/useToast';

// Basic qualifying-info options. These are the fit filter, not a survey —
// keep the list short.
const ORG_TYPES = ['commercial', 'government', 'academic', 'other'] as const;
const ASSET_COUNTS = ['none', '1-5', '6-50', '50+'] as const;

// Validation schema for access requests
const accessRequestSchema = z.object({
  email: z
    .string()
    .trim()
    .email({ message: 'Invalid email address' })
    .max(254, { message: 'Email must be less than 254 characters' }),
  // Free-form "biggest operational bottleneck" (stored as message).
  message: z
    .string()
    .trim()
    .max(1000, { message: 'Message must be less than 1000 characters' })
    .optional()
    .default(''),
  org_type: z.enum(ORG_TYPES).optional(),
  asset_count: z.enum(ASSET_COUNTS).optional(),
});

interface AlphaGateProps {
  children: React.ReactNode;
}

type TabType = 'magic' | 'request';

// Check bypass conditions outside component to avoid hook issues
const shouldBypassAuth = (): boolean => {
  const skipAuthEnv = import.meta.env.VITE_SKIP_AUTH;

  if (skipAuthEnv === 'true') return true;
  if (skipAuthEnv === 'false') return false;

  if ((import.meta.env as Record<string, unknown>).VITEST) {
    return false;
  }

  return import.meta.env.DEV;
};

const AlphaGate: React.FC<AlphaGateProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [orgType, setOrgType] = useState('');
  const [assetCount, setAssetCount] = useState('');
  const [honeypot, setHoneypot] = useState(''); // bot trap — must stay empty
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('magic');
  const toast = useToast();

  const skipAuth = shouldBypassAuth();
  const supabaseConfigured = isSupabaseConfigured();

  const checkSession = useCallback(async () => {
    if (!supabaseConfigured || skipAuth) {
      setIsLoading(false);
      return;
    }

    try {
      if (!supabase) {
        setIsLoading(false);
        return;
      }
      const {
        data: { session },
      } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    } catch (error) {
      console.error('Session check error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [supabaseConfigured, skipAuth]);

  useEffect(() => {
    if (skipAuth || !supabaseConfigured) {
      setIsLoading(false);
      return;
    }

    checkSession();

    if (!supabase) {
      setIsLoading(false);
      return;
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (import.meta.env.DEV) {
        console.log('Auth state change:', event);
      }
      setIsAuthenticated(!!session);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [skipAuth, supabaseConfigured, checkSession]);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabaseConfigured) return;

    setIsSubmitting(true);

    try {
      if (!supabase) return;
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;

      toast.success('Magic Link sent! Check your email to access the app.');
      setEmail('');
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unable to send magic link';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabaseConfigured) return;

    setIsSubmitting(true);

    try {
      const validatedData = accessRequestSchema.parse({
        email: email.trim(),
        message: message.trim(),
        org_type: orgType || undefined,
        asset_count: assetCount || undefined,
      });

      if (!supabase) return;
      const { error } = await supabase.functions.invoke(
        'submit-access-request',
        {
          // `website` is a honeypot field — real users leave it empty.
          body: { ...validatedData, website: honeypot },
        }
      );

      if (error) {
        throw new Error(error.message || 'Failed to submit access request');
      }

      toast.success(
        'Access request submitted! You will be contacted via email when approved.'
      );
      setEmail('');
      setMessage('');
      setOrgType('');
      setAssetCount('');
      setHoneypot('');
    } catch (error: unknown) {
      console.error('Access request error:', error);

      if (error instanceof z.ZodError) {
        const errorMessage = error.issues[0]?.message || 'Invalid input';
        toast.error(errorMessage);
      } else {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unable to submit access request. Please try again.';
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    if (!supabaseConfigured || !supabase) return;

    await supabase.auth.signOut();
    setIsAuthenticated(false);
    toast.info('Signed out successfully');
  };

  // Authentication bypass for local testing
  if (skipAuth) {
    console.log('Authentication bypassed for testing');
    return <>{children}</>;
  }

  // Check if Supabase is configured
  if (!supabaseConfigured) {
    console.warn('Supabase not configured - showing app without auth');
    return <>{children}</>;
  }

  // Loading state - retro style
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-retro-bg">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-retro-amber border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-retro-dim font-mono text-sm uppercase tracking-wider">
            Initializing...
          </p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login UI with retro mission control style
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-retro-bg p-4">
        {/* Grid background effect */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,204,102,0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,204,102,0.1) 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
          }}
        />

        {/* Login card */}
        <div className="w-full max-w-md bg-retro-surface border border-retro-border rounded-lg shadow-2xl overflow-hidden relative z-10">
          {/* Header */}
          <div className="border-b border-retro-border p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <Rocket className="w-12 h-12 text-retro-amber" />
                <Lock className="w-5 h-5 text-retro-cyan absolute -bottom-1 -right-1" />
              </div>
            </div>
            <h1 className="text-xl font-mono font-light text-retro-amber tracking-widest uppercase">
              Space Operator
            </h1>
            <div className="flex items-center justify-center gap-2 mt-2">
              <div className="h-px flex-1 bg-retro-border" />
              <span className="text-xs font-mono text-retro-dim uppercase tracking-wider">
                Alpha Access
              </span>
              <div className="h-px flex-1 bg-retro-border" />
            </div>
          </div>

          {/* Tabs */}
          <div className="px-4 pt-4">
            <div className="flex bg-retro-bg rounded border border-retro-border">
              <button
                onClick={() => setActiveTab('magic')}
                className={`flex-1 py-2.5 px-4 font-mono text-xs uppercase tracking-wider transition-colors ${
                  activeTab === 'magic'
                    ? 'bg-retro-surface text-retro-amber border-b-2 border-retro-amber'
                    : 'text-retro-dim hover:text-retro-muted'
                }`}
              >
                Magic Link
              </button>
              <button
                onClick={() => setActiveTab('request')}
                className={`flex-1 py-2.5 px-4 font-mono text-xs uppercase tracking-wider transition-colors ${
                  activeTab === 'request'
                    ? 'bg-retro-surface text-retro-cyan border-b-2 border-retro-cyan'
                    : 'text-retro-dim hover:text-retro-muted'
                }`}
              >
                Request Access
              </button>
            </div>
          </div>

          {/* Tab content */}
          <div className="p-6">
            {activeTab === 'magic' && (
              <form onSubmit={handleMagicLink} className="space-y-4">
                <div className="space-y-2">
                  <label
                    htmlFor="magic-email"
                    className="block text-xs font-mono uppercase tracking-wider text-retro-muted"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-retro-dim" />
                    <input
                      id="magic-email"
                      type="email"
                      placeholder="pilot@astrowrangler.space"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-retro-bg border border-retro-border text-retro-muted placeholder:text-retro-dim pl-10 pr-4 py-2.5 rounded font-mono text-sm focus:outline-none focus:border-retro-amber focus:ring-1 focus:ring-retro-amber/30"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <p className="text-xs text-retro-dim font-mono">
                    ▸ Secure login link sent to your email
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-retro-amber/10 border border-retro-amber text-retro-amber font-mono text-sm uppercase tracking-wider py-2.5 px-4 rounded hover:bg-retro-amber/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Magic Link
                    </>
                  )}
                </button>
              </form>
            )}

            {activeTab === 'request' && (
              <form onSubmit={handleRequestAccess} className="space-y-4">
                {/* Honeypot: hidden from users, tempting to bots. Kept off-screen
                    rather than display:none so naive bots still "see" it. */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />
                <div className="space-y-2">
                  <label
                    htmlFor="request-email"
                    className="block text-xs font-mono uppercase tracking-wider text-retro-muted"
                  >
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-retro-dim" />
                    <input
                      id="request-email"
                      type="email"
                      placeholder="pilot@astrowrangler.space"
                      value={email}
                      onChange={(e) => setEmail(e.target.value.trim())}
                      className="w-full bg-retro-bg border border-retro-border text-retro-muted placeholder:text-retro-dim pl-10 pr-4 py-2.5 rounded font-mono text-sm focus:outline-none focus:border-retro-cyan focus:ring-1 focus:ring-retro-cyan/30"
                      maxLength={254}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                {/* Basic qualifying info — short fit filter, not a survey. */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label
                      htmlFor="request-org-type"
                      className="block text-xs font-mono uppercase tracking-wider text-retro-muted"
                    >
                      Org Type
                    </label>
                    <select
                      id="request-org-type"
                      value={orgType}
                      onChange={(e) => setOrgType(e.target.value)}
                      className="w-full bg-retro-bg border border-retro-border text-retro-muted px-3 py-2.5 rounded font-mono text-sm focus:outline-none focus:border-retro-cyan focus:ring-1 focus:ring-retro-cyan/30"
                      disabled={isSubmitting}
                    >
                      <option value="">Select…</option>
                      <option value="commercial">Commercial</option>
                      <option value="government">Government / Defense</option>
                      <option value="academic">Academic</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="request-asset-count"
                      className="block text-xs font-mono uppercase tracking-wider text-retro-muted"
                    >
                      Spacecraft Flown
                    </label>
                    <select
                      id="request-asset-count"
                      value={assetCount}
                      onChange={(e) => setAssetCount(e.target.value)}
                      className="w-full bg-retro-bg border border-retro-border text-retro-muted px-3 py-2.5 rounded font-mono text-sm focus:outline-none focus:border-retro-cyan focus:ring-1 focus:ring-retro-cyan/30"
                      disabled={isSubmitting}
                    >
                      <option value="">Select…</option>
                      <option value="none">None yet</option>
                      <option value="1-5">1–5</option>
                      <option value="6-50">6–50</option>
                      <option value="50+">50+</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="request-message"
                    className="block text-xs font-mono uppercase tracking-wider text-retro-muted"
                  >
                    Biggest Operational Bottleneck
                  </label>
                  <textarea
                    id="request-message"
                    placeholder="What slows your ops down most today? (conjunction triage, maneuver planning, scattered data…)"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full bg-retro-bg border border-retro-border text-retro-muted placeholder:text-retro-dim px-4 py-2.5 rounded font-mono text-sm focus:outline-none focus:border-retro-cyan focus:ring-1 focus:ring-retro-cyan/30 min-h-[80px] resize-none"
                    maxLength={1000}
                    disabled={isSubmitting}
                  />
                  <div className="flex justify-end">
                    <p className="text-xs text-retro-dim font-mono">
                      {message.length}/1000
                    </p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-retro-cyan/10 border border-retro-cyan text-retro-cyan font-mono text-sm uppercase tracking-wider py-2.5 px-4 rounded hover:bg-retro-cyan/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      Request Access
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-retro-border">
              <div className="flex items-center justify-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-retro-cyan animate-pulse" />
                <p className="text-xs text-retro-dim font-mono uppercase tracking-wider">
                  Private Alpha Build
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Version indicator */}
        <div className="fixed bottom-4 left-4 text-xs font-mono text-retro-subtle">
          v0.1.0-alpha
        </div>
      </div>
    );
  }

  // Authenticated - render children with sign out button
  return (
    <>
      {children}
      {/* Sign out button - retro style */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={handleSignOut}
          className="text-retro-dim hover:text-retro-muted font-mono text-xs uppercase tracking-wider px-3 py-1.5 rounded border border-retro-border hover:border-retro-dim hover:bg-retro-surface transition-colors"
        >
          Sign Out
        </button>
      </div>
    </>
  );
};

export default AlphaGate;
